import {ActiveParticipantService} from '@app/services/participants/active-participant.service';
import {
	AfterViewInit,
	Component,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import {
	NzTableData,
	NzTablePaginationPosition,
	NzTableSize
} from 'ng-zorro-antd/table/src/table.types';
import { ParticipantService} from '@app/services/participants/participant.service';
import { ProjectService } from '@app/services/project/project.service';
import {ProtectedPrivacyService} from '@app/services/ui/protected-privacy.service';
import {participantPages} from '../participants.routes';
import {ActivatedRoute, Router} from '@angular/router';
import { Subscription } from 'rxjs';
import {IParticipant, IParticipantConfig} from '@interfaces/interfaces';
import { InterpolationService, TextInterpolation } from 'src/app/services/interpolation/interpolation.service';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { NzTableSortFn, NzTableSortOrder } from 'ng-zorro-antd/table';
import {ExportConfig} from '@api/projects/interfaces';
import {NzConfigService} from 'ng-zorro-antd/core/config';
import {TParticipantSearchResult} from '@app/services/participants/interfaces';
import {SelectExportsButtonComponent} from '@app/components/elements/exports/select-exports-button.component';
import { MenuService } from '@app/services/menu';
import { PermissionService } from '@app/services/permission/permission.service';

class DynamicColumn {
	sortOrder: NzTableSortOrder | null;
	sortFn: NzTableSortFn | null;
	filterVisible: boolean;
	filterSearchValue: string;

	constructor(
		public header: string,
		public interpolation: TextInterpolation,
		public width?: string,
	) {
		this.filterSearchValue = '';
		this.filterVisible = false;
	}

	interpolate(data: IParticipant): string {
		return this.interpolation.interpolate(data.data);
	}
}

@Component({
	selector: 'app-participant-list',
	templateUrl: './participant-list.component.html',
	styleUrls: ['./participant-list.component.scss']
})
export class ParticipantListComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild('customTplTooMany', { static: false }) customTplTooMany?: TemplateRef<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
	@ViewChild('customTplEmpty', { static: false }) customTplEmpty?: TemplateRef<any>; // eslint-disable-line @typescript-eslint/no-explicit-any

	public simple = false;
	public loading = true;
	public pagination = true;
	public position: NzTablePaginationPosition = 'bottom';
	public header = true;
	public title = false;
	public expandable = true;
	public checkbox = false;
	public allChecked = false;
	public footer = false;
	public bordered = false;
	public sizeChanger = false;
	public size: NzTableSize = 'small';
	public privacyMode = false;
	public hasProject = false;
	public participantAddSplitButton: SplitButtonElement = {
		title: 'participants.add',
		icon: 'plus',
		type: 'default',
		callback: () => this.showParticipantCreate(),
	};
	public uidFilterValue = '';
	public uidFilterVisible = false;
	public createdFilterValue = '';
	public createdFilterVisible = false;
	public setOfCheckedId = new Set<string>();
	public checked = false;
	public indeterminate = false;
	public configs: (ExportConfig & { active?: boolean })[];
	hasParticipantsCreatePermission = false;

	autofocusTableSearch = false;
	listOfData: IParticipant[] = [];
	listOfDisplayData: IParticipant[] = [];

	dynamicColumns: DynamicColumn[];
	private subscription = new Subscription();
	private latestParticipantTimeoutMS = 300000; // in ms, 1min => 60000, 5min => 300000,...

	@ViewChild(SelectExportsButtonComponent)
	public exportButton: SelectExportsButtonComponent;

	constructor(
		private activeParticipantService: ActiveParticipantService,
		private interpolation: InterpolationService,
		private participantService: ParticipantService,
		private privacyService: ProtectedPrivacyService,
		private projectService: ProjectService,
		private router: Router,
		private nzConfigService: NzConfigService,
		private route: ActivatedRoute,
		private menuService: MenuService,
		private permissionService: PermissionService,
	) {

	}

	ngOnInit(): void {
		this.subscription.add(this.projectService.participantsConfig$.subscribe(config => this.showWorkflowList(config)));
		this.subscription.add(this.privacyService.toggleChange$().subscribe(status => this.updatePrivacyStatus(status) ));
		this.subscription.add(this.participantService.participantsOfActiveProject$.subscribe(data => this.saveParticipantsList(data)));
		this.subscription.add(this.projectService.participantsConfig$.subscribe(config => this.loadParticipantDetailInfos(config)));
		this.subscription.add(this.projectService.activeProject$.subscribe(project => {
			this.hasProject = !!project;
			if (!project) {
				this.loading = false;
			}
		}));
		this.subscription.add(this.projectService.exportsConfig$.subscribe(
			configs => {
				this.configs = configs?.filter(c => c?.showInParticipantOverview);
				for (const c of this.configs) {
					c.active = true;
				}
			}
		));
		this.subscription.add(this.permissionService.hasProjectPermission$('participants:create').subscribe(hasParticipantsCreatePermission => {
			this.hasParticipantsCreatePermission = hasParticipantsCreatePermission;
		}));
		this.subscription.add(this.route.fragment.subscribe((fragment: string) => {
			this.autofocusTableSearch = fragment === 'search';
		}));
		this.subscription.add(this.projectService.projects$.subscribe(projects => {
			if (projects.length === 0) {
				this.loading = false;
			}
		}));
		this.menuService.title = 'person.participants';
	}

	ngAfterViewInit(): void {
		this.nzConfigService.set('empty', { nzDefaultEmptyContent: this.customTplEmpty });
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	private async updatePrivacyStatus(status: boolean): Promise<void> {
		this.nzConfigService.set('empty', { nzDefaultEmptyContent: this.customTplEmpty });
		this.privacyMode = status;

		if (this.privacyMode) {
			this.listOfData = [];
		}
		else {
			const {results} = await this.participantService.searchMetaByString('', false);
			this.listOfData = results;
		}
		this.listOfDisplayData = this.listOfData;
	}

	private saveParticipantsList(data: IParticipant[]): void {
		if (this.privacyMode && data?.length !== 1) {
			this.loading = false;
			this.listOfData = [];
			return;
		}

		const list = [];
		list.push( ...data );

		this.listOfData = list;
		list.forEach(
			item => item.formatDate = new Date(item.creationTimestamp).toLocaleDateString(undefined, {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			})
		);
		this.setNewParticipant();
		this.loading = false;
		this.listOfDisplayData = this.listOfData;
	}

	private loadParticipantDetailInfos(config: IParticipantConfig) {
		const list = config?.details?.list || [];
		this.dynamicColumns = list.map(column => {
			const interpolation = this.interpolation.interpolation(column.data);
			const newColumn = new DynamicColumn(column.header, interpolation, column.width);
			newColumn.sortFn = (a: IParticipant, b: IParticipant) => newColumn.interpolate(a).localeCompare(newColumn.interpolate(b));
			return newColumn;
		});
	}

	currentPageDataChange(event): void {

	}

	showParticipantProfil(participant: IParticipant): void {
		this.activeParticipantService.activeParticipant = participant;
		this.router
			.navigate([participantPages.stammDaten.base()])
			.catch(console.error);
	}

	showParticipantCreate(): void {
		this.router
			.navigate([participantPages.create.base()])
			.catch(console.error);
	}

	compareUid(a: NzTableData, b: NzTableData): number {
		return a._id.localeCompare(b._id);
	}

	compareCreationTime(a: NzTableData, b: NzTableData): number {
		let createTimeA = a.creationTimestamp;
		if (!createTimeA) {
			createTimeA = 0;
		}
		let createTimeB = b.creationTimestamp;
		if (!createTimeB) {
			createTimeB = 0;
		}
		return createTimeA - createTimeB;
	}

	private setNewParticipant(): void {
		if (this.listOfData.length > 0) {
			let latestEntry = this.listOfData[0];
			this.listOfData.forEach((data) => {
				if (data?.creationTimestamp > (latestEntry?.creationTimestamp || 0)) {
					latestEntry = data;
				}
			});
			if (Date.now() - (latestEntry?.creationTimestamp || 0) <= this.latestParticipantTimeoutMS) {
				latestEntry['newest'] = true;
			}
		}
	}

	filterSearch(column: DynamicColumn): void {
		if (!column || column.filterSearchValue === '') {
			this.listOfDisplayData = this.listOfData;
		}
		else {
			this.dynamicColumns.forEach(listColumn => {
				if (listColumn !== column) {
					listColumn.filterSearchValue = '';
				}
			});
			this.listOfDisplayData = this.listOfData.filter(
				(item: IParticipant) => column.interpolate(item).toLowerCase().includes(column.filterSearchValue.toLowerCase())
			);
		}
	}

	filterUid(): void {
		if (this.uidFilterValue === '') {
			this.listOfDisplayData = this.listOfData;
		}
		this.listOfDisplayData = this.listOfData.filter(
			(item: IParticipant) => item.id.toLowerCase().includes(this.uidFilterValue.toLowerCase())
		);
	}

	filterCreated(): void {
		if (this.createdFilterValue === '') {
			this.listOfDisplayData = this.listOfData;
		}
		this.listOfDisplayData = this.listOfData.filter(
			(item: IParticipant & {formatDate?: string}) => item.formatDate?.includes(this.createdFilterValue.toLowerCase())
		);
	}

	refreshCheckedStatus(): void {
		this.checked = this.listOfDisplayData.every(({ id }) => this.setOfCheckedId.has(id));
		this.indeterminate = this.listOfDisplayData.some(({ id }) => this.setOfCheckedId.has(id)) && !this.checked;
		this.exportButton.updateButtons();
	}

	onItemChecked(id: string, checked: boolean): void {
		this.updateCheckedSet(id, checked);
		this.refreshCheckedStatus();
	}

	onAllChecked(checked: boolean): void {
		this.listOfDisplayData.forEach(({ id }) => this.updateCheckedSet(id, checked));
		this.refreshCheckedStatus();
	}

	updateCheckedSet(id: string, checked: boolean): void {
		if (checked) {
			this.setOfCheckedId.add(id);
		} else {
			this.setOfCheckedId.delete(id);
		}
	}

	updateSearchResults(searchResult: TParticipantSearchResult): void {
		this.saveParticipantsList(searchResult.results || []);
		this.updateEmptyTableStyle(searchResult.status);
	}

	updateTableIsLoadingStatus(status: boolean): void {
		this.loading = status;
	}

	private updateEmptyTableStyle(status: 'valid' | 'unspecific' | 'invalid') {
		if (status === 'unspecific') {
			this.nzConfigService.set('empty', { nzDefaultEmptyContent: this.customTplTooMany });
		}
		else {
			this.nzConfigService.set('empty', { nzDefaultEmptyContent: this.customTplEmpty });
		}
	}
	private showWorkflowList(config: IParticipantConfig): void {
		const usedParticipantList = config?.details?.usedParticipantList || '';
		if (usedParticipantList === 'verlaufsdiagramm') {
			this.router.navigate([participantPages.workflowDashboard.base()]).catch(console.error);
		}
	}
}
